import React, { useEffect, useRef, useState } from "react";
import api from "../../api/axios";
import { useTranslation } from "react-i18next";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";


const buildFriendlyAiError = (raw, t) => {
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim().replace(/[.]+$/g, "");
  if (!trimmed) return "";
  const normalized = trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
  return t("photo_request_modal.errors.ai_reason_prefix", {
    defaultValue: "Please upload another photo because {{reason}}.",
    reason: normalized,
  });
};

const CROP = 224;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

/**
 * PhotoRequestModal
 *
 * Shows before joining an event if the user has no profile photo.
 * Styling is intentionally aligned with ConfirmAttendanceModal.
 *
 * Props:
 * - visible: boolean
 * - onClose: () => void
 * - onUploaded?: (url: string) => void
 */
const PhotoRequestModal = ({
  visible = false,
  onClose,
  onUploaded,
  titleOverride = "",
  subtitleOverride = "",
  allowClose = true,
}) => {
  const { t, i18n } = useTranslation("events");

  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null); // cache-busted URL for UI
  const [uploadedRawUrl, setUploadedRawUrl] = useState(null); // raw URL for callback

  // Cropper state (local client-side editing, like in AvatarModal)
  const [fileSrc, setFileSrc] = useState(null); // Object URL of selected image
  const [imgReady, setImgReady] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [baseScale, setBaseScale] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const imgDomRef = useRef(null);

  const dragStartRef = useRef({ x: 0, y: 0 });
  const cropRef = useRef(null);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);

  const fileInputRef = useRef(null);

  const hasPreview = Boolean(previewUrl);
  const isCropping = Boolean(fileSrc);

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const distance = (p1, p2) => Math.hypot(p1.x - p2.x, p1.y - p2.y);

  const getScaledSize = (z) => {
    const baseW = imgSize.w * baseScale;
    const baseH = imgSize.h * baseScale;
    return { dw: baseW * z, dh: baseH * z };
  };

  const clampPosFor = (x, y, z) => {
    const { dw, dh } = getScaledSize(z);
    const minX = Math.min(0, CROP - dw);
    const minY = Math.min(0, CROP - dh);
    const maxX = 0;
    const maxY = 0;
    return { x: clamp(x, minX, maxX), y: clamp(y, minY, maxY) };
  };

  const applyZoomAt = (newZ, cx, cy) => {
    const z = clamp(newZ, ZOOM_MIN, ZOOM_MAX);
    const baseW = imgSize.w * baseScale;
    const baseH = imgSize.h * baseScale;
    if (!baseW || !baseH) {
      setZoom(z);
      return;
    }
    const sOld = baseScale * zoom;
    const sNew = baseScale * z;
    setPos((prevPos) => {
      const ix = (cx - prevPos.x) / sOld;
      const iy = (cy - prevPos.y) / sOld;
      const nx = cx - ix * sNew;
      const ny = cy - iy * sNew;
      return clampPosFor(nx, ny, z);
    });
    setZoom(z);
  };

  useEffect(() => {
    if (!visible) {
      setUploading(false);
      setErrorMsg("");
      setPreviewUrl(null);
      setUploadedRawUrl(null);

      if (fileSrc) {
        try {
          URL.revokeObjectURL(fileSrc);
        } catch {
          // ignore
        }
      }
      setFileSrc(null);
      imgDomRef.current = null;
      setImgReady(false);
      setImgSize({ w: 0, h: 0 });
      setBaseScale(1);
      setZoom(1);
      setPos({ x: 0, y: 0 });
      setDragging(false);

      // clear value so selecting the same file again triggers change
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [visible, fileSrc]);

  if (!visible) return null;

  const title = titleOverride || t("photo_request_modal.title", {
    defaultValue: "Set up your photo",
  });
  const subtitle = subtitleOverride || t("photo_request_modal.subtitle", {
    defaultValue: "To join events you should set up your photo",
  });

  const whyTitle = t("photo_request_modal.why_title", {
    defaultValue: "It helps:",
  });
  const why1 = t("photo_request_modal.why_1", {
    defaultValue: "Other participants find you at the venue",
  });
  const why2 = t("photo_request_modal.why_2", {
    defaultValue: "Make VaultGrid more secure",
  });
  const why3 = t("photo_request_modal.why_3", {
    defaultValue: "Recommend better events and contacts for you",
  });

  const uploadLabel = uploading
    ? t("photo_request_modal.uploading", { defaultValue: "Uploading…" })
    : t("photo_request_modal.upload", { defaultValue: "Upload" });
  const changeLabel = t("photo_request_modal.change", {
    defaultValue: "Change photo",
  });
  const continueLabel = t("photo_request_modal.continue", {
    defaultValue: "Continue",
  });

  const openPicker = () => {
    if (uploading) return;
    setErrorMsg("");
    fileInputRef.current?.click();
  };

  const handleRequestClose = () => {
    if (uploading) return;
    if (!allowClose) return;
    onClose?.();
  };

  const handleContinue = () => {
    // Final confirmation: only after the photo has been successfully uploaded.
    const finalUrl = uploadedRawUrl || previewUrl;
    if (!finalUrl) return;
    onUploaded?.(finalUrl);
    onClose?.();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic client-side guard
    if (!file.type?.startsWith("image/")) {
      const msg = t("photo_request_modal.errors.not_image", {
        defaultValue: "Please select an image file.",
      });
      setErrorMsg(msg);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg } })
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      const msg = t("photo_request_modal.errors.file_too_large", {
        defaultValue: "File is too large. Please choose an image under 10 MB.",
      });
      setErrorMsg(msg);
      return;
    }

    // Reset previous state
    setErrorMsg("");
    setPreviewUrl(null);
    setUploadedRawUrl(null);

    if (fileSrc) {
      try {
        URL.revokeObjectURL(fileSrc);
      } catch {
        // ignore
      }
    }

    // Reset cropper state
    setImgReady(false);
    setImgSize({ w: 0, h: 0 });
    setBaseScale(1);
    setZoom(1);
    setPos({ x: 0, y: 0 });
    setDragging(false);
    imgDomRef.current = null;

    const url = URL.createObjectURL(file);
    setFileSrc(url);
  };

  // Handler to run when the image DOM element loads
  const handleImageLoad = (e) => {
    const img = e.currentTarget;
    imgDomRef.current = img;

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return;

    setImgSize({ w, h });

    const cover = CROP / Math.min(w, h);
    setBaseScale(cover);
    setZoom(1);

    const bw = w * cover;
    const bh = h * cover;
    setPos({ x: (CROP - bw) / 2, y: (CROP - bh) / 2 });

    setImgReady(true);
  };

  const onPointerDown = (e) => {
    if (!isCropping || !fileSrc || !imgReady) return;
    e.preventDefault();
    const target = e.currentTarget;
    try {
      target.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    const rect = target.getBoundingClientRect();
    const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    pointersRef.current.set(e.pointerId, pt);

    if (pointersRef.current.size === 1) {
      setDragging(true);
      dragStartRef.current = { x: e.clientX, y: e.clientY };
    } else if (pointersRef.current.size === 2) {
      const [p1, p2] = Array.from(pointersRef.current.values());
      const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      pinchRef.current = {
        startDist: distance(p1, p2),
        startZoom: zoom,
        center,
      };
      setDragging(false);
    }
  };

  const onPointerMove = (e) => {
    if (!isCropping || !fileSrc || !imgReady) return;
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const pt = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, pt);
    }

    if (pointersRef.current.size === 1 && dragging) {
      e.preventDefault();
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      setPos((p) => clampPosFor(p.x + dx, p.y + dy, zoom));
    } else if (pointersRef.current.size === 2 && pinchRef.current) {
      e.preventDefault();
      const [p1, p2] = Array.from(pointersRef.current.values());
      const dist = distance(p1, p2);
      if (pinchRef.current.startDist > 0) {
        const scale = dist / pinchRef.current.startDist;
        const newZ = clamp(
          pinchRef.current.startZoom * scale,
          ZOOM_MIN,
          ZOOM_MAX
        );
        applyZoomAt(newZ, pinchRef.current.center.x, pinchRef.current.center.y);
      }
    }
  };

  const onPointerUp = (e) => {
    if (!isCropping || !fileSrc || !imgReady) return;
    const target = e.currentTarget;
    try {
      target.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) {
      setDragging(false);
    } else if (pointersRef.current.size === 1) {
      const rect = target.getBoundingClientRect();
      const [onlyPt] = Array.from(pointersRef.current.values());
      dragStartRef.current = {
        x: onlyPt.x + rect.left,
        y: onlyPt.y + rect.top,
      };
      setDragging(true);
    }
  };

  const exportAndUpload = async () => {
    const img = imgDomRef.current;
    if (!img || !isCropping) return;
    try {
      setUploading(true);
      setErrorMsg("");

      const canvas = document.createElement("canvas");
      const SIZE = 512;
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d");

      const factor = SIZE / CROP;
      const s = baseScale * zoom;
      const dw = imgSize.w * s * factor;
      const dh = imgSize.h * s * factor;
      const dx = pos.x * factor;
      const dy = pos.y * factor;

      ctx.clearRect(0, 0, SIZE, SIZE);
      ctx.drawImage(img, dx, dy, dw, dh);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/webp", 0.85)
      );
      if (!blob) {
        const msg = t("photo_request_modal.errors.upload_failed", {
          defaultValue: "Failed to upload photo",
        });
        setErrorMsg(msg);
        return;
      }

      const form = new FormData();
      // Backend expects the field name "file", same as in AvatarModal.
      form.append("file", new File([blob], "avatar.webp", { type: "image/webp" }));
      const uiLanguage = String(i18n.resolvedLanguage || i18n.language || "en")
        .trim()
        .toLowerCase()
        .split("-")[0];

      const { data } = await api.post("/accounts/upload-avatar/", form, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-UI-Language": ["en", "ru", "sk"].includes(uiLanguage) ? uiLanguage : "en",
        },
      });

      const rawUrl =
        data?.avatar_custom_url || data?.url || data?.avatar_url || null;
      if (!rawUrl) {
        const msg = t("photo_request_modal.errors.no_url", {
          defaultValue: "Upload succeeded, but no URL was returned.",
        });
        setErrorMsg(msg);
        window.dispatchEvent(
          new CustomEvent("global-error", { detail: { message: msg } })
        );
        return;
      }

      const busted =
        rawUrl.indexOf("?") >= 0
          ? `${rawUrl}&t=${Date.now()}`
          : `${rawUrl}?t=${Date.now()}`;

      setUploadedRawUrl(rawUrl);
      setPreviewUrl(busted);

      // Notify the whole app (e.g. GlobalPhotoGuard) that avatar has been updated.
      // App can refresh/patch the user profile and hide any global photo guard.
      try {
        window.dispatchEvent(
          new CustomEvent("vg-avatar-updated", { detail: { avatar_custom_url: rawUrl } })
        );
      } catch {
        // ignore
      }

      onUploaded?.(rawUrl);
      onClose?.();

      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: t("photo_request_modal.success", {
              defaultValue: "Photo uploaded successfully",
            }),
          },
        })
      );

      // Cleanup cropper state, keep preview
      if (fileSrc) {
        try {
          URL.revokeObjectURL(fileSrc);
        } catch {
          // ignore
        }
      }
      setFileSrc(null);
      setImgReady(false);
      setImgSize({ w: 0, h: 0 });
      setBaseScale(1);
      setZoom(1);
      setPos({ x: 0, y: 0 });
      setDragging(false);
      imgDomRef.current = null;
    } catch (err) {
      const status = err?.response?.status || 500;
      const backendMsg = err?.response?.data?.error;
      let msg;
      if (backendMsg) {
        const friendly = buildFriendlyAiError(backendMsg, t);
        msg = friendly || backendMsg;
      } else {
        msg =
          err?.message ||
          t("photo_request_modal.errors.upload_failed", {
            defaultValue: "Failed to upload photo",
          });
      }

      setErrorMsg(msg);

      // For validation errors (4xx) we only show inline text.
      // For server errors we also show a global toast.
      if (!err?.response || status >= 500) {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { message: msg, status },
          })
        );
      }
    } finally {
      setUploading(false);
      // allow re-selecting the same file
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const modalFooter = isCropping ? (
    <div className="space-y-3">
      <AppModalButton
        type="button"
        onClick={exportAndUpload}
        disabled={uploading || !imgReady}
        loading={uploading}
      >
        {uploadLabel}
      </AppModalButton>

      <AppModalButton
        type="button"
        variant="secondary"
        onClick={() => {
          if (fileSrc) {
            try {
              URL.revokeObjectURL(fileSrc);
            } catch {
              // ignore
            }
          }
          setFileSrc(null);
          imgDomRef.current = null;
          setImgReady(false);
          setImgSize({ w: 0, h: 0 });
          setBaseScale(1);
          setZoom(1);
          setPos({ x: 0, y: 0 });
          setDragging(false);
          setErrorMsg("");
          openPicker();
        }}
        disabled={uploading}
      >
        {changeLabel}
      </AppModalButton>
    </div>
  ) : !hasPreview ? (
    <AppModalButton
      type="button"
      onClick={openPicker}
      disabled={uploading}
      loading={uploading}
    >
      {uploadLabel}
    </AppModalButton>
  ) : (
    <div className="space-y-3">
      <AppModalButton
        type="button"
        variant="secondary"
        onClick={openPicker}
        disabled={uploading}
      >
        {changeLabel}
      </AppModalButton>

      <AppModalButton
        type="button"
        onClick={handleContinue}
        disabled={uploading || (!uploadedRawUrl && !previewUrl)}
      >
        {continueLabel}
      </AppModalButton>
    </div>
  );

  return (
    <AppModalShell
      open={visible}
      title={title}
      description={!isCropping ? subtitle : ""}
      onClose={allowClose ? handleRequestClose : undefined}
      rightAction={allowClose ? null : <div className="h-11 w-11" aria-hidden="true" />}
      closeOnBackdropClick={allowClose}
      closeAriaLabel={t("photo_request_modal.close_aria", { defaultValue: "Close" })}
      maxWidthClassName="max-w-[32rem]"
      desktopMaxHeight="min(88vh, 680px)"
      footer={modalFooter}
      bodyClassName="pb-4"
    >
      {errorMsg && (
        <p className="rounded-2xl border border-red-300/25 bg-red-500/16 px-4 py-3 text-center text-sm font-medium text-red-100">
          {errorMsg}
        </p>
      )}

      {/* Main content: reasons list, cropper, or final preview */}
      {isCropping ? (
        <div className="mt-4 flex flex-col items-center">
          <div
            ref={cropRef}
            className={
              "mx-auto mb-4 rounded-full overflow-hidden bg-black select-none relative " +
              (dragging ? "cursor-grabbing" : "cursor-grab")
            }
            style={{ width: CROP, height: CROP, touchAction: "none" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
            onWheel={(e) => {
              if (!fileSrc || !imgReady) return;
              e.preventDefault();
              const rect = e.currentTarget.getBoundingClientRect();
              const cx = e.clientX - rect.left;
              const cy = e.clientY - rect.top;
              const factor = Math.exp(-e.deltaY / 400);
              const newZ = clamp(zoom * factor, ZOOM_MIN, ZOOM_MAX);
              applyZoomAt(newZ, cx, cy);
            }}
          >
            {fileSrc ? (
              (() => {
                const baseW = imgSize.w * baseScale || CROP;
                const baseH = imgSize.h * baseScale || CROP;
                return (
                  <div
                    className="absolute top-0 left-0"
                    style={{
                      width: baseW,
                      height: baseH,
                      transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`,
                      transformOrigin: "top left",
                      willChange: "transform",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        width: baseW,
                        height: baseH,
                        transform: `scale(${zoom})`,
                        transformOrigin: "top left",
                        willChange: "transform",
                        pointerEvents: "none",
                      }}
                    >
                      <img
                        src={fileSrc}
                        alt={t("photo_request_modal.crop_alt", { defaultValue: "Photo crop preview" })}
                        draggable={false}
                        onLoad={handleImageLoad}
                        className="select-none block"
                        style={{
                          width: baseW,
                          height: baseH,
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="absolute inset-0" />
            )}
          </div>

          <div className="mb-2 w-full max-w-[260px]">
            <input
              type="range"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={0.01}
              value={zoom}
              onChange={(ev) => {
                const newZ = parseFloat(ev.target.value);
                applyZoomAt(newZ, CROP / 2, CROP / 2);
              }}
              className="w-full"
              disabled={!imgReady || uploading}
            />
          </div>
        </div>
      ) : hasPreview ? (
        <div className="mt-5 flex justify-center">
          <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-2 border-white/80 shadow-lg">
            <img
              src={previewUrl}
              alt={t("photo_request_modal.preview_alt", {
                defaultValue: "Uploaded photo",
              })}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-4">
          <p className="text-white font-extrabold text-xl mb-3">{whyTitle}</p>
          <div className="text-white font-semibold text-base leading-relaxed space-y-1 opacity-95">
            <p>• {why1}</p>
            <p>• {why2}</p>
            <p>• {why3}</p>
          </div>
        </div>
      )}

      {/* hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </AppModalShell>
  );
};

export default PhotoRequestModal;
